import {Component, DestroyRef, inject} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ReactiveFormsModule} from '@angular/forms';
import {finalize} from 'rxjs';
import {NzFormModule} from 'ng-zorro-antd/form';
import {NZ_MODAL_DATA, NzModalRef} from 'ng-zorro-antd/modal';
import {NzNotificationService} from 'ng-zorro-antd/notification';
import {AuthFlowService} from '../../../core/services/auth-flow.service';
import {PsdFields} from '../../../shared/psd-fields/psd-fields';
import {createPsdForm} from '../../../shared/psd-fields/psd-form';

export interface SetPasswordWithXmlModalData {
  xml: string;
}

@Component({
  selector: 'app-set-password-with-xml-modal',
  imports: [
    ReactiveFormsModule,
    NzFormModule,
    PsdFields,
  ],
  templateUrl: './set-password-with-xml-modal.html',
})
export class SetPasswordWithXmlModal {
  private readonly translate = inject(TranslateService);
  private readonly _data = inject<SetPasswordWithXmlModalData>(NZ_MODAL_DATA);
  private readonly _modalRef = inject(NzModalRef);
  private readonly _authFlow = inject(AuthFlowService);
  private readonly _notification = inject(NzNotificationService);
  private readonly _destroyRef = inject(DestroyRef);

  readonly form = createPsdForm();

  constructor() {
    this.form.statusChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this._modalRef.updateConfig({nzOkDisabled: this.form.invalid}));
  }

  handleOk(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const {newPsd, confirmPsd} = this.form.getRawValue();
    this._modalRef.updateConfig({nzOkLoading: true, nzCancelDisabled: true});

    this._authFlow.changePsd({xml: this._data.xml, newPsd, confirmPsd})
      .pipe(finalize(() => this._modalRef.updateConfig({nzOkLoading: false})), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this._modalRef.close(true);
          this._notification.success(this.translate.instant('common.success'), this.translate.instant('resetPsd.changed'));
        },
        error: () => {
          this._modalRef.updateConfig({nzCancelDisabled: false});
        }
      });
  }
}
